import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Box,
  Divider,
  Skeleton,
  Alert,
  Paper,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useThumbzApi } from "src/services/thumbz-api";
import { IApproval, IApprovalNotificationLogResponseDto } from "src/services/thumbz-base-api";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { Email, WhatsApp, Close } from "@mui/icons-material";
import { useBillingStatus } from "src/hooks/use-billing";

interface ApprovalLogProps {
  approval: IApproval;
}

export const ApprovalLog: React.FC<ApprovalLogProps> = ({ approval }) => {
  const theme = useTheme();
  const { api } = useThumbzApi();
  const { data: billing } = useBillingStatus();
  const isFreePlan = String(billing.plan?.slug).toLowerCase() === "free";

  const { apv_id, notificationConfig } = approval;
  const { data, isLoading, isError } = useQuery<IApprovalNotificationLogResponseDto[]>({
    queryKey: ["approvalNotificationLog", apv_id],
    queryFn: () => api.approvalNotificationLog.approvalNotificationLogControllerList({ apv_id }),
    enabled: !!apv_id,
  });

  // Group logs by date (YYYY-MM-DD)
  const groupedLogs = useMemo(() => {
    if (!data) return {};

    return data.reduce<Record<string, IApprovalNotificationLogResponseDto[]>>((acc, log) => {
      const date = new Date(log.anl_sent_at).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(log);
      return acc;
    }, {});
  }, [data]);

  // Helper to get status icon and color
  const getStatusInfo = (status: string) => {
    switch (status) {
      case "Enviado":
        return {
          icon: <CheckCircleOutlineIcon />,
          color: theme.palette.success.main,
        };
      case "Pulado":
        return {
          icon: <SkipNextIcon />,
          color: theme.palette.warning.main,
        };
      case "Falhou":
        return {
          icon: <ErrorOutlineIcon />,
          color: theme.palette.error.main,
        };
      default:
        return {
          icon: null,
          color: theme.palette.text.secondary,
        };
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Notificações de Aprovação
        </Typography>
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      </Stack>
    );
  }

  // Render error state
  if (isError) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Notificações de Aprovação
        </Typography>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Erro ao carregar notificações.
        </Alert>
      </Stack>
    );
  }

  // Render empty state
  if (!data || data.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Notificações de Aprovação
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 3,
            textAlign: "center",
            borderColor: theme.palette.divider,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhuma notificação configurada.
          </Typography>
        </Paper>
      </Stack>
    );
  }

  // Notification config card (if exists)
  const renderNotificationConfig = () => {
    if (!notificationConfig) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 2,
            textAlign: "center",
            borderColor: theme.palette.divider,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhuma configuração de notificação encontrada para esta aprovação.
          </Typography>
        </Paper>
      );
    }

    let whatsappEnabled = notificationConfig.anc_whatsapp_enabled === true;
    if (isFreePlan) {
      whatsappEnabled = false;
    }

    const title = whatsappEnabled
      ? "Canal habilitado"
      : isFreePlan
        ? "Recurso indisponível no plano gratuito"
        : "Não configurado";

    return (
      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={0} mt={-1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={<Email sx={{ color: "#fff", p: 0.5 }} />}
                label="Email"
                size="small"
                color={notificationConfig.anc_email_enabled ? "success" : "default"}
                variant={notificationConfig.anc_email_enabled ? "filled" : "outlined"}
                sx={{
                  opacity: notificationConfig.anc_email_enabled ? 1 : 0.5,
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: "#fff",
                    p: 0.5,
                  },
                }}
                title={
                  notificationConfig.anc_email_enabled ? "Canal habilitado" : "Não configurado"
                }
              />
              <Tooltip title={title} arrow>
                <Chip
                  icon={
                    isFreePlan ? (
                      <Close sx={{ color: "#fff", p: 0.5 }} />
                    ) : (
                      <WhatsApp sx={{ color: "#fff", p: 0.5 }} />
                    )
                  }
                  label="WhatsApp"
                  size="small"
                  color={whatsappEnabled ? "success" : "default"}
                  variant={whatsappEnabled ? "filled" : "outlined"}
                  sx={{
                    opacity: whatsappEnabled ? 1 : 0.5,
                    fontWeight: 500,
                    "& .MuiChip-icon": {
                      p: 0.5,
                    },
                  }}
                  title={title}
                />
              </Tooltip>
            </Stack>
            {!notificationConfig.anc_email_enabled && !whatsappEnabled && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Nenhum canal de notificação está configurado para esta aprovação.
              </Typography>
            )}

            {/* Coluna - Detalhes do agendamento */}
            {notificationConfig.anc_email_enabled || whatsappEnabled ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">
                    Intervalo (dias):
                  </Box>{" "}
                  {notificationConfig.anc_interval_days ?? "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">
                    Máx. lembretes:
                  </Box>{" "}
                  {notificationConfig.anc_max_reminders ?? "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight="bold">
                    Horário preferido:
                  </Box>{" "}
                  {notificationConfig.anc_preferred_time ?? "-"}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Stack spacing={4}>
      <Stack>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Configuração de Notificações
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Configurações de notificações para esta aprovação.
        </Typography>

        {renderNotificationConfig()}
      </Stack>

      <Stack>
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Notificações de Aprovação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Histórico das notificações enviadas para os clientes desta aprovação.
        </Typography>

        {Object.entries(groupedLogs).map(([date, logs]) => (
          <Box key={date} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle2">{date}</Typography>
              <Divider sx={{ flex: 1, ml: 2 }} />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 2,
              }}
            >
              {logs.map((log) => {
                const statusInfo = getStatusInfo(log.anl_status);

                return (
                  <Card
                    key={log.anl_id}
                    variant="outlined"
                    sx={{
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <CardContent sx={{ p: 2, pb: 2 }}>
                      <Stack spacing={0}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={log.anl_type}
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          />
                          <Chip
                            size="small"
                            label={log.anl_delivery_method}
                            color="secondary"
                            sx={{
                              fontWeight: 500,
                              bgcolor:
                                log.anl_delivery_method === "Whatsapp"
                                  ? "#25D366" // WhatsApp green
                                  : theme.palette.grey[400],
                              color: "#fff",
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mt: 0.5,
                            pr: 4, // Make room for the status icon
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {log.cust_name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {log.cust_email}
                        </Typography>

                        {log.cust_mobile_phone && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {log.cust_mobile_phone}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            color: statusInfo.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {statusInfo.icon}
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            position: "absolute",
                            left: 16,
                            bottom: 8,
                            fontSize: 12,
                          }}
                        >
                          Enviado em: {new Date(log.anl_sent_at).toLocaleTimeString()}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};
